import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import type { DropdownItem } from "@/modules/dropdowns/types";
import type {
  CreateVaultPayload,
  RevealableField,
  UpdateVaultPayload,
  VaultComment,
  VaultEntryDetail,
  VaultListParams,
  VaultListResponse,
  VaultLogsResponse,
  VaultStats,
} from "../types";

const VAULT_KEY = ["vault"] as const;
const STATS_KEY = ["vault", "stats"] as const;

export function useVaultList(
  params: VaultListParams = {},
): UseQueryResult<VaultListResponse> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...VAULT_KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<VaultListResponse>("/vault", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          category: params.category,
          sensitivity: params.sensitivity,
          companyId: params.companyId,
          productId: params.productId,
          projectId: params.projectId,
          isExternal:
            typeof params.isExternal === "boolean"
              ? String(params.isExternal)
              : undefined,
          expiringDays: params.expiringDays,
        },
      }),
  });
}

export function useVaultEntry(id: string): UseQueryResult<VaultEntryDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...VAULT_KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ entry: VaultEntryDetail }>(`/vault/${id}`, {
        token: accessToken,
      });
      return res.entry;
    },
  });
}

export function useVaultStats(): UseQueryResult<VaultStats> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...STATS_KEY, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<VaultStats>("/vault/stats", { token: accessToken }),
  });
}

export function useCreateVaultEntry() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateVaultPayload) =>
      apiFetch<{ entry: VaultEntryDetail }>("/vault", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: VAULT_KEY });
      qc.invalidateQueries({ queryKey: STATS_KEY });
    },
  });
}

export function useUpdateVaultEntry(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateVaultPayload) =>
      apiFetch<{ entry: VaultEntryDetail }>(`/vault/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: VAULT_KEY });
      qc.invalidateQueries({ queryKey: STATS_KEY });
    },
  });
}

export function useDeleteVaultEntry() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/vault/${id}`, { method: "DELETE", token: accessToken }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: VAULT_KEY });
      qc.invalidateQueries({ queryKey: STATS_KEY });
    },
  });
}

export function useRevealField(vaultId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (field: RevealableField) =>
      apiFetch<{ value: string }>(`/vault/${vaultId}/reveal`, {
        method: "POST",
        body: { field },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...VAULT_KEY, vaultId, "logs"] });
    },
  });
}

export function useCopyField(vaultId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (field: RevealableField) => {
      const res = await apiFetch<{ value: string }>(`/vault/${vaultId}/copy`, {
        method: "POST",
        body: { field },
        token: accessToken,
      });
      try {
        await navigator.clipboard.writeText(res.value);
        window.setTimeout(() => {
          navigator.clipboard
            .writeText("")
            .catch(() => {
              /* clipboard might be unavailable */
            });
        }, 30_000);
      } catch {
        /* clipboard write failed */
      }
      return res;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...VAULT_KEY, vaultId, "logs"] });
    },
  });
}

export function useVaultLogs(
  vaultId: string,
  page = 1,
  limit = 10,
): UseQueryResult<VaultLogsResponse> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [
      ...VAULT_KEY,
      vaultId,
      "logs",
      page,
      limit,
      accessToken ? "auth" : "anon",
    ],
    enabled: !!accessToken && !!vaultId,
    queryFn: () =>
      apiFetch<VaultLogsResponse>(`/vault/${vaultId}/logs`, {
        token: accessToken,
        query: { page, limit },
      }),
  });
}

export function useVaultComments(vaultId: string): UseQueryResult<VaultComment[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [
      ...VAULT_KEY,
      vaultId,
      "comments",
      accessToken ? "auth" : "anon",
    ],
    enabled: !!accessToken && !!vaultId,
    queryFn: async () => {
      const res = await apiFetch<{ data: VaultComment[] }>(
        `/vault/${vaultId}/comments`,
        { token: accessToken },
      );
      return res.data;
    },
  });
}

export function useAddVaultComment(vaultId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      apiFetch<{ comment: VaultComment }>(`/vault/${vaultId}/comments`, {
        method: "POST",
        body: { content },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...VAULT_KEY, vaultId, "comments"],
      });
    },
  });
}

export function useDeleteVaultComment(vaultId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      apiFetch(`/vault/${vaultId}/comments/${commentId}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...VAULT_KEY, vaultId, "comments"],
      });
    },
  });
}

export function useVaultCategories(): UseQueryResult<DropdownItem[]> {
  return useCategoryItems("vault_category");
}

export function useVaultSensitivities(): UseQueryResult<DropdownItem[]> {
  return useCategoryItems("vault_sensitivity");
}
